import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { propertyAPI, getUploadUrl } from '../../lib/api';
import { formatCurrency, formatCommissionRate, isFixedCommission } from '../../lib/utils';
import { GlassCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Building2, MapPin, Search, Filter } from 'lucide-react';

const PropertyList = () => {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    property_type: searchParams.get('type') || '',
    min_price: searchParams.get('min') || '',
    max_price: searchParams.get('max') || '',
  });

  useEffect(() => { loadProperties(); }, []);

  const loadProperties = async () => {
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.property_type) params.property_type = filters.property_type;
      if (filters.min_price) params.min_price = parseFloat(filters.min_price);
      if (filters.max_price) params.max_price = parseFloat(filters.max_price);
      const res = await propertyAPI.getProperties(params);
      setProperties(res.data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const propertyTypes = [
    { value: 'condominium', label: 'Condominium' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'landed', label: 'Landed House' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
  ];

  return (
    <div className="space-y-6" data-testid="property-list">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Property Listings</h1>
        <p className="text-gray-500 mt-1">Discover your perfect property</p>
      </div>

      {/* Filters */}
      <GlassCard className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search by name or location..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10"
              data-testid="property-search"
            />
          </div>
          <Select
            value={filters.property_type || "all"}
            onValueChange={(value) => setFilters({ ...filters, property_type: value === "all" ? "" : value })}
          >
            <SelectTrigger className="w-full md:w-48" data-testid="property-type-filter">
              <SelectValue placeholder="Property Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {propertyTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            placeholder="Min Price"
            value={filters.min_price}
            onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
            className="w-full md:w-32"
          />
          <Input
            type="number"
            placeholder="Max Price"
            value={filters.max_price}
            onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
            className="w-full md:w-32"
          />
          <Button onClick={loadProperties} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="search-btn">
            <Filter className="w-4 h-4 mr-2" />Apply
          </Button>
        </div>
      </GlassCard>

      {/* Property Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
        </div>
      ) : properties.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => (
            <Link key={property.id} to={`/properties/${property.id}`} data-testid={`property-card-${property.id}`}>
              <GlassCard hover className="p-0 overflow-hidden h-full">
                <div
                  className="h-48 bg-cover bg-center relative bg-gray-200"
                  style={{
                    backgroundImage: property.images?.[0]
                      ? `url(${getUploadUrl(property.images[0])})`
                      : 'url(https://images.unsplash.com/photo-1709501580562-91a9415917cc?crop=entropy&cs=srgb&fm=jpg&q=85)',
                  }}
                >
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-[#4E50FF] text-white capitalize">
                      {property.property_type}
                    </span>
                  </div>
                  {property.commission_percentage != null && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/90 text-emerald-700">
                        {isFixedCommission(property)
                          ? `${formatCommissionRate(property, { showSuffix: false })} Comm (Fixed)`
                          : `${property.commission_percentage}% Comm`}
                      </span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="text-white font-mono font-semibold">
                      {formatCurrency(property.price_range_min)} - {formatCurrency(property.price_range_max)}
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{property.name}</h3>
                  <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    {property.location}
                  </div>
                  {property.address && (
                    <p className="text-xs text-gray-400 mb-2 line-clamp-1">{property.address}</p>
                  )}
                  <p className="text-sm text-gray-400 line-clamp-2">{property.description}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500">{property.total_units} units</span>
                    <span className="text-xs text-gray-500">by {property.developer}</span>
                  </div>
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      ) : (
        <GlassCard className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Properties Found</h3>
          <p className="text-gray-500">Try adjusting your search filters</p>
        </GlassCard>
      )}
    </div>
  );
};

export default PropertyList;
